import { useResponsiveStyles } from '../../../utils/useResponsiveStyles';
import { styles } from '.';
import { ButtonVariantStyles } from '../../../components/DynamicButton/DynamicButton.styles';
import { ButtonVariant, HEADER_STATE, hexToRgba, pxToNumber } from '../../Commons';

export const useHeaderStyles = ({
  globalStyles,
  localStyles,
  background,
  rightButton,
  headerState,
  scrolled,
  builderMode,
  isDesktop,
  isActive,
  isWholeNavbarParsed,
  designPreview,
}: any) => {
  const rightButtonVariant: ButtonVariant = rightButton?.variant?.value;
  const buttonVariantStyles = ButtonVariantStyles?.[rightButtonVariant || ButtonVariant.PRIMARY];
  const { createResponsiveStyles } = useResponsiveStyles(isDesktop, localStyles, globalStyles);

  const navLinkStyles = {
    ...styles.NavLink,
    ...globalStyles.bodyText,
    ...localStyles.navbarLinkStyles,
    lineHeight: '30px',
  };

  const normalizedFontSize = (localStyles?.navbarLinkStyles?.fontSize ?? globalStyles?.bodyText?.fontSize) || undefined;
  const navLinksTextStylesResponsive = {
    ...createResponsiveStyles(navLinkStyles, 'navbarLinkStyles', 'bodyText'),
    ...(normalizedFontSize ? { fontSize: normalizedFontSize } : {}),
  } as const;

  const navLinkStyles_Mobile = (isChild: boolean) => ({
    ...navLinksTextStylesResponsive,
    lineHeight: 1,
    justifyContent: 'space-between',
    height: isChild ? '30px' : '55px',
  });
  const navLinksContainerStyles = {
    ...styles.NavLinksContainer,
    visibility: isDesktop && isWholeNavbarParsed ? 'visible' : 'hidden',
    '@media (max-width: 780px)': {
      ...styles.NavLinksContainer['@media (max-width: 780px)'],
      ...(globalStyles?.backgroundColor?.length > 0 && {
        backgroundColor: hexToRgba(globalStyles.backgroundColor, 0.5),
      }),
      ...(background?.value?.length > 0 && { backgroundColor: hexToRgba(background.value, 0.5) }),
    },
  };

  const navLinkContainerStyles = (isActive: boolean, isDesktop: boolean) => {
    return isActive
      ? isDesktop
        ? navLinksContainerStyles
        : { ...navLinksContainerMobileStyles(isActive), ...menuActiveMobileStyles }
      : isDesktop
      ? navLinksContainerStyles
      : navLinksContainerMobileStyles(isActive);
  };

  const navLinksContainerMobileStyles = (isActive: boolean) => ({
    ...styles.NavLinksContainer_Mobile,
    ...(globalStyles?.backgroundColor?.length > 0 && {
      backgroundColor: hexToRgba(globalStyles.backgroundColor, 0.5),
    }),
    ...(background?.value?.length > 0 && { backgroundColor: hexToRgba(background.value, 0.5) }),
  });

  const IconStyles = {
    ...styles.Icon,
    ...localStyles.navbarLinkStyles,
  };

  const buttonStyles = {
    textDecoration: 'none',
    ...buttonVariantStyles,
    ...globalStyles.buttonVariants?.[rightButton?.variant?.value || ButtonVariant.PRIMARY],
    ...localStyles.buttonVariants?.[rightButton?.variant?.value || ButtonVariant.PRIMARY],
    maxWidth: '100%',
    margin: '20px 0',
    borderRadius: `${localStyles.buttonRadius ?? globalStyles.buttonRadius}px`,
    fontFamily: globalStyles.bodyText.fontFamily,
    padding: isDesktop ? '12px 24px' : '5px 20px',
    fontSize: isDesktop ? '18px' : '12px',
    '@media (max-width: 780px)': {
      fontSize: '12px',
      padding: '5px 20px',
    },
  };

  const dropdownContainerStyles = {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    cursor: 'pointer',
    height: '70px',
  };

  const dropdownButtonStyles = {
    ...navLinksTextStylesResponsive,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
  };

  const dropdownMenuStyles = (isScrolled: boolean) => ({
    position: 'absolute',
    top: '70px',
    right: 0,
    backgroundColor: isScrolled ? background.value || globalStyles.backgroundColor : 'rgba(219, 210, 210, 0.27)',
    boxShadow: '0 4px 8px rgba(0,0,0,0.5)',
    borderRadius: '4px',
    minWidth: '200px',
    zIndex: 210,
  });

  const fontSize = pxToNumber(
    (navLinksTextStylesResponsive && 'fontSize' in navLinksTextStylesResponsive
      ? (navLinksTextStylesResponsive as any).fontSize
      : '') || ''
  );

  const dropdownMobileItemStyles = () => ({
    ...navLinksTextStylesResponsive,
    display: 'block',
    color: (navLinksTextStylesResponsive as any)?.color ?? navLinkStyles.color,
    paddingLeft: `30px`,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    lineHeight: 1,
    width: '100%',
    fontSize: `${(fontSize || 0) - 3}px`,
    marginTop: '10px',
  });

  const dropdownItemStyles = (isChild?: boolean, hasChildren?: boolean, isSubChild?: boolean, isLast?: boolean) => ({
    ...navLinksTextStylesResponsive,
    borderBottom:
      (isChild && isLast) || (isSubChild && !isLast) || hasChildren ? '0' : `1px solid ${navLinkStyles.color}`,
    display: 'block',
    // color: navLinkStyles.color,
    color: navLinkStyles.color,
    backgroundColor: background.value || globalStyles.backgroundColor,
    padding: `12px 16px 12px  ${isSubChild ? '40px' : '16px'}`,
    whiteSpace: 'nowrap',
    textDecoration: 'none',
    lineHeight: 1,
    width: '100%',
    ...(isSubChild && { fontSize: `${(fontSize || 0) - 3}px` }),
    // '&:hover': {
    //   backgroundColor: navLinkStyles.color,
    //   color: background.value || globalStyles.backgroundColor,
    // },
  });

  const menuActiveMobileStyles = {
    ...styles.MenuActive_Mobile,
    ...(globalStyles?.backgroundColor?.length > 0 && {
      backgroundColor: hexToRgba(globalStyles.backgroundColor, 0.8),
    }),
    ...(background?.value?.length > 0 && { backgroundColor: hexToRgba(background.value, 0.8) }),
  };

  const getHeaderStyles = () => {
    let baseStyles: any = isDesktop ? styles.Navbar : styles.Navbar_Mobile;

    // Helper function to calculate width based on builder mode and design preview
    const getHeaderWidth = () => {
      if (!builderMode) return isActive ? '100%' : !isDesktop ? '95%' : '100%';
      if (designPreview && typeof window !== 'undefined') {
        return isDesktop
          ? headerState === HEADER_STATE.DEFAULT
            ? '100%'
            : window.innerWidth > 1536
            ? '94.7%'
            : '93.3%'
          : isActive
          ? '410px'
          : '390px';
      }

      return isDesktop
        ? isActive
          ? '100%'
          : !isDesktop
          ? '95%'
          : '100%'
        : designPreview === undefined
        ? '100%'
        : isActive
        ? '410px'
        : '390px';
    };

    // Helper function to get common fixed position styles
    const getFixedPositionStyles = () => {
      const fixedStyles: any = {
        position: designPreview === undefined ? 'absolute' : ('fixed' as const),
        width: designPreview === undefined ? (isActive ? '100%' : !isDesktop ? '95%' : '100%') : getHeaderWidth(),
      };

      // Apply responsive width adjustments for all cases
      if (isDesktop) {
        fixedStyles['@media (max-width: 1200px)'] = {
          width: designPreview ? '92%' : isActive ? '100%' : !isDesktop ? '95%' : '100%',
        };
      }

      return fixedStyles;
    };

    const getHeaderHeight = () => {
      if (isDesktop) {
        return scrolled || builderMode ? '100px' : '110px';
      }
      return '80px';
    };

    switch (headerState) {
      case HEADER_STATE.STICKY:
        baseStyles = {
          ...baseStyles,
          ...getFixedPositionStyles(),
          top: builderMode ? 'inherit' : isActive ? '0px' : isDesktop ? '0px' : '10px',
          left: isDesktop
            ? 'inherit'
            : builderMode
            ? designPreview === undefined
              ? '49%'
              : designPreview === true
              ? '50%'
              : '61.5%'
            : '50%',
        };
        break;

      case HEADER_STATE.SCROLL_UP:
        baseStyles = {
          ...baseStyles,
          ...getFixedPositionStyles(),
          transition: 'height 0.3s ease-in-out',
          height: getHeaderHeight(),
          background: !isDesktop
            ? background.value || globalStyles.backgroundColor
            : scrolled
            ? background.value || globalStyles.backgroundColor
            : 'transparent',
          left: isDesktop
            ? 'inherit'
            : builderMode
            ? designPreview === undefined
              ? '49%'
              : designPreview === true
              ? '50%'
              : '61.5%'
            : '50%',
          top: builderMode ? 'inherit' : isActive ? '0px' : isDesktop ? '0px' : '10px',
          '@media (max-width: 780px)': {
            height: '80px',
          },
        };
        break;

      default:
        baseStyles = {
          ...baseStyles,
          width: designPreview === undefined ? (isActive ? '100%' : !isDesktop ? '95%' : '100%') : getHeaderWidth(),
          top: builderMode ? 'inherit' : isActive ? '0px' : isDesktop ? '0px' : '10px',
          left: isDesktop ? 'inherit' : builderMode ? (designPreview === undefined ? '49%' : '50%') : '50%',
          position: isDesktop ? 'relative' : 'absolute',
        };
    }

    return {
      ...baseStyles,
      ...(globalStyles?.backgroundColor?.length > 0 && { backgroundColor: globalStyles.backgroundColor }),
      ...(background?.value?.length > 0 && { backgroundColor: background.value }),
      ...(headerState === HEADER_STATE.SCROLL_UP && ((!scrolled && isDesktop) || designPreview)
        ? { backgroundColor: 'transparent' }
        : { backgroundColor: background.value || globalStyles.backgroundColor }),
      marginTop: !builderMode ? 'inherit' : isDesktop ? '0px' : isActive ? '0' : '10px !important',
    };
  };

  const navlinkWrapperStyles = (isDropdown: boolean, isDesktop: boolean) => {
    return {
      ...styles.NavlinkWrapper,
      width: isDesktop ? 'auto' : '100%',
      flexDirection: isDesktop ? 'row' : 'column',
      alignItems: isDesktop ? 'center' : 'normal',
      height: isDropdown || !isDesktop ? 'auto' : '70px',
    };
  };

  return {
    navLinkContainerStyles,
    navLinksContainerMobileStyles,
    IconStyles,
    buttonStyles,
    navLinkStyles: navLinksTextStylesResponsive,
    navLinkStyles_Mobile,
    dropdownContainerStyles,
    dropdownButtonStyles,
    dropdownMenuStyles,
    dropdownItemStyles,
    dropdownMobileItemStyles,
    menuActiveMobileStyles,
    getHeaderStyles,
    navlinkWrapperStyles,
  };
};
